import i18n from '@/utils/i18n';
import moment from 'moment';
import {
  AutoPaySetting,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '@/utils/money';
import { useCallback, useEffect, useMemo, useState } from 'react';

const monthlyFrequency = {
  1: i18n.t('autopay.option.frequency.monthly_start'),
  15: i18n.t('autopay.option.frequency.monthly_middle'),
  31: i18n.t('autopay.option.frequency.monthly_end'),
};

export interface MappedAutoPaySetting {
  description: string;
  frequency: string;
  uuid: string;
}

export const useAutopaySettings = () => {
  const {
    autoPaySelfServeEnabled,
    chatAutoPaySetting,
    currencyCode,
    supplierAutoPaySettings,
  } = usePaymentsContext();

  const mapAutoPaySetting = useCallback(
    (setting: AutoPaySetting): MappedAutoPaySetting => {
      const isOrderDate = setting.pay_based_on === 'INVOICE_DATE';
      const description = setting.max_amount_in_micros
        ? i18n.t(
            isOrderDate
              ? 'autopay.option.description.order_date'
              : 'autopay.option.description',
            {
              amount: formatCurrency(
                setting.max_amount_in_micros,
                currencyCode ?? DEFAULT_CURRENCY_CODE,
              ),
            },
          )
        : i18n.t(
            isOrderDate
              ? 'autopay.option.description.order_date.no_max'
              : 'autopay.option.description.no_max',
          );

      let frequency = '';
      if (setting.frequency.type === 'DAILY') {
        frequency = i18n.t('autopay.option.frequency.daily');
      } else if (setting.frequency.type === 'WEEKLY') {
        frequency = i18n.t('autopay.option.frequency.weekly', {
          day: moment().day(setting.frequency.day_of_week).format('dddd'),
        });
      } else if (setting.frequency.type === 'BIWEEKLY') {
        frequency = i18n.t('autopay.option.frequency.biweekly', {
          day: moment().day(setting.frequency.day_of_week).format('dddd'),
        });
      } else if (setting.frequency.type === 'MONTHLY') {
        frequency =
          monthlyFrequency[setting.frequency.day_of_month as 1 | 15 | 31];
      }

      return {
        description,
        frequency,
        uuid: setting.uuid,
      };
    },
    [currencyCode],
  );

  const availableAutoPaySettings = useMemo(() => {
    if (!autoPaySelfServeEnabled) {
      return chatAutoPaySetting ? [mapAutoPaySetting(chatAutoPaySetting)] : [];
    }

    return supplierAutoPaySettings
      .filter(
        (setting) =>
          setting.is_visible_to_restaurants ||
          setting.uuid === chatAutoPaySetting?.uuid,
      )
      .map(mapAutoPaySetting);
  }, [
    autoPaySelfServeEnabled,
    chatAutoPaySetting,
    mapAutoPaySetting,
    supplierAutoPaySettings,
  ]);
  const [selectedAutoPaySettingUUID, setSelectedAutoPaySettingUUID] = useState(
    chatAutoPaySetting?.uuid,
  );

  useEffect(() => {
    if (!selectedAutoPaySettingUUID && chatAutoPaySetting?.uuid) {
      setSelectedAutoPaySettingUUID(chatAutoPaySetting?.uuid);
    }
  }, [chatAutoPaySetting, selectedAutoPaySettingUUID]);

  const shouldSaveAutoPaySetting = useMemo(() => {
    return (
      Boolean(selectedAutoPaySettingUUID) &&
      selectedAutoPaySettingUUID !== chatAutoPaySetting?.uuid
    );
  }, [chatAutoPaySetting, selectedAutoPaySettingUUID]);

  return useMemo(
    () => ({
      availableAutoPaySettings,
      hasSavedAutoPaySetting: Boolean(chatAutoPaySetting),
      selectedAutoPaySettingUUID,
      setSelectedAutoPaySettingUUID,
      shouldSaveAutoPaySetting,
    }),
    [
      availableAutoPaySettings,
      chatAutoPaySetting,
      selectedAutoPaySettingUUID,
      shouldSaveAutoPaySetting,
    ],
  );
};
